import React, { useState, useCallback } from "react";
import DownloadButton from "../../../Public/assets/img/DownloadButton.svg";
import { CustomDatePicker } from "../../../Components/DatePicker";
import {
  AudFlag,
  BGPFlag,
  CadFlag,
  EuroFlag,
  SGDFlag,
  usFlag,
} from "../../../Public/assets/index";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../Components/ui/select";
import { accountStatementDownload } from "../../../api";
import { getSession } from "../../../axios.config";
import ButtonLoader from "../../../Components/Loader";

const AccountStatement = ({ globalData }) => {
  const user = JSON.parse(getSession("userId"));

  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setDate(today.getDate() - 30);

  const formatDateDisplay = useCallback((date) => {
    if (!date) return "";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${months[date.getMonth()]} ${String(date.getDate()).padStart(2, "0")}, ${date.getFullYear().toString().slice(-2)}`;
  }, []);

  const formatDateAPI = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const currencies = [
    { code: "USD", flag: usFlag },
    { code: "GBP", flag: BGPFlag },
    { code: "SGD", flag: SGDFlag },
    { code: "CAD", flag: CadFlag },
    { code: "EUR", flag: EuroFlag },
    { code: "AUD", flag: AudFlag },
  ];

  const [fromDate, setFromDate] = useState(oneMonthAgo);
  const [toDate, setToDate] = useState(today);
  const [displayCalendar, setDisplayCalendar] = useState(false);
  const [displayToCalendar, setDisplayToCalendar] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(currencies[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hideCalendar = () => {
    setDisplayCalendar(false);
    setDisplayToCalendar(false);
  };

  const handleSelect = (date, type) => {
    if (type === "from") {
      setFromDate(date);
      if (toDate && date > toDate) {
        setError("Please make sure 'start' date is earlier than your 'end' date.");
      } else {
        setError("");
      }
    } else {
      if (date < fromDate) {
        setError("Please make sure 'end' date is after than your 'start' date.");
        return;
      }
      setToDate(date);
      setError("");
    }

    hideCalendar();
  };

  const handleDownload = async () => {
    if (loading) return;

    if (toDate < fromDate) {
        setError("Please make sure 'start' date is earlier than your 'end' date.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const resp = await accountStatementDownload(
        user?.userId,
        globalData?.data?.accountType,
        formatDateAPI(fromDate),
        formatDateAPI(toDate),
        selectedCurrency?.code
      );

      if (resp?.status === 200 && resp?.data) {
        const blob = new Blob([resp.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `Statement_${selectedCurrency.code}.pdf`;
        link.click();

        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Download Error:", err);
    }
    setLoading(false);
  };

  return (
    <div className="mt-[40px] mb-[40px]">
      <div className="text-blackShade font-geomanistBold text-[20px]">
        Account statement
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:space-x-6">
        <div>
          <div className="text-[14px] text-[rgb(118,124,143)] mt-[10px] mb-[5px]">
            Select Dates
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <CustomDatePicker
              date={formatDateDisplay(fromDate)}
              text="From"
              onChange={(date) => handleSelect(date, "from")}
              maxDate={new Date()}
              displayCalendar={displayCalendar}
              setDisplayCalendar={setDisplayCalendar}
            />

            <CustomDatePicker
              date={formatDateDisplay(toDate)}
              text="To"
              onChange={(date) => handleSelect(date, "to")}
              maxDate={new Date()}
              minDate={fromDate}
              displayCalendar={displayToCalendar}
              setDisplayCalendar={setDisplayToCalendar}
            />
          </div>
         
        </div>

        <div>
          <div className="text-[14px] text-[rgb(118,124,143)] mt-[10px] mb-[5px]">
            Filter by Currency
          </div>

          <Select
            value={selectedCurrency?.code}
            onValueChange={(value) => {
              const selected = currencies.find((c) => c.code === value);
              setSelectedCurrency(selected);
            }}
          >
            <SelectTrigger className="w-full sm:w-[140px] p-2">
              <SelectValue>
                <div className="flex items-center gap-2">
                  <img src={selectedCurrency.flag} className="w-5 h-5" />
                  {selectedCurrency.code}
                </div>
              </SelectValue>
            </SelectTrigger>

            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  <div className="flex items-center gap-2">
                    <img src={c.flag} className="w-5 h-5" />
                    {c.code}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
 {error && (
            <div className="text-red-500 text-sm mt-2">{error}</div>
          )}
      <div
        onClick={handleDownload}
        className={`mt-[20px] w-[150px] h-[40px] rounded flex  ${
          loading ? "bg-[#0061D3] cursor-not-allowed opacity-50 flex items-center justify-center" : "cursor-pointer"
        }`}
      >
        {loading ? (
          <ButtonLoader />
        ) : (
          <img src={DownloadButton} alt="download" />
        )}
      </div>
    </div>
  );
};

export default AccountStatement;
